import React from 'react';
import { X, Mail, Phone, Calendar, Hash, BookOpen, Building2, Users, Key } from 'lucide-react';

const InfoRow = ({ icon: Icon, label, value, accent, mono }) => (
  <div className="flex items-center gap-3 py-3">
    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${accent}10` }}>
      <Icon className="w-4 h-4" style={{ color: accent }} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">{label}</p>
      <p className={`text-sm text-gray-800 truncate ${mono ? 'font-mono text-xs' : ''}`}>{value}</p>
    </div>
  </div>
);

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' }) : null;

/**
 * Unified Profile Card used across Department and Institution dashboards
 * @param {object} person - User data (full_name, email, id, role, phone, etc.)
 * @param {string} accent - Theme accent color
 * @param {string} role - Display role label ("Teacher", "Student", "Class Teacher", etc.)
 * @param {function} onClose - Close handler
 * @param {React.ReactNode} actions - Optional bottom action buttons (Block/Unblock etc.)
 */
export function ProfileCard({ person, accent, role, onClose, actions }) {
  if (!person) return null;
  const A = accent || '#34A853';
  const name = person.full_name || person.student_name || person.name || 'Unknown';
  const email = person.email || person.student_email || '';
  const initial = (name || '?')[0].toUpperCase();
  const uid = person.id || person.student_id || '';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="profile-card-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="h-28 relative" style={{ background: `linear-gradient(135deg, ${A}, ${A}cc)` }}>
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors" data-testid="close-profile-card">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        <div className="flex justify-center -mt-14">
          <div className="w-28 h-28 rounded-full border-4 border-white shadow-lg flex items-center justify-center text-white text-3xl font-bold" style={{ background: A }}>
            {initial}
          </div>
        </div>
        <div className="text-center mt-3 px-6">
          <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{name}</h3>
          <p className="text-xs text-gray-400 mt-0.5 uppercase tracking-wider font-medium">{role || 'User'}</p>
        </div>
        <div className="px-6 mt-5 mb-2 space-y-0 divide-y divide-gray-100">
          {email && <InfoRow icon={Mail} label="Email" value={email} accent={A} />}
          {uid && <InfoRow icon={Hash} label="ID" value={uid} accent={A} mono />}
          {person.phone && <InfoRow icon={Phone} label="Phone" value={person.phone} accent={A} />}
          {person.department_names && <InfoRow icon={Building2} label="Department" value={person.department_names} accent={A} />}
          {person.class_name && <InfoRow icon={BookOpen} label="Class" value={person.class_name} accent={A} />}
          {(person.classes_count != null) && <InfoRow icon={Users} label="Classes" value={`${person.classes_count} class${person.classes_count !== 1 ? 'es' : ''}`} accent={A} />}
          {person.assigned_at && <InfoRow icon={Calendar} label="Assigned" value={formatDate(person.assigned_at)} accent={A} />}
          {person.created_at && !person.assigned_at && <InfoRow icon={Calendar} label="Joined" value={formatDate(person.created_at)} accent={A} />}
          {person.licence_key && <InfoRow icon={Key} label="Licence Key" value={person.licence_key} accent={A} mono />}
        </div>
        {actions && (
          <div className="px-6 py-4 border-t border-gray-100">
            {actions}
          </div>
        )}
        {!actions && <div className="h-4" />}
      </div>
    </div>
  );
}

export default ProfileCard;
